// Shared custom claims schema (K8s Pulse, Quizio, future tools):
//
// {
//   "role": "admin" | "program_manager" | "mentor" | "learner",
//   "memberships": [
//     { "tag": "KCNA-005", "role": "mentor" },
//     { "tag": "CKAD-002", "role": "learner" }
//   ]
// }
//
// role = highest privilege (for quick access checks, Firestore rules, nav routing)
// memberships = per-PLG detail (for scoped views, exam eligibility)
//
// | Custom Claim      | K8s Pulse              | Quizio                          |
// |-------------------|------------------------|---------------------------------|
// | admin             | Full access            | Full platform                   |
// | program_manager   | Full dashboard         | Manage candidates, exams, etc.  |
// | mentor            | Own PLG(s) only        | Read-only: candidates, sessions |
// | learner           | Own profile only       | Candidate: take exams           |

export interface Membership {
  tag: string;   // e.g. "KCNA-005"
  role: string;  // "mentor" | "learner"
}

// Program managers — email fallback before claims are set
const PROGRAM_MANAGER_EMAILS = (import.meta.env.VITE_PROGRAM_MANAGER_EMAILS || "")
  .split(",")
  .map((e: string) => e.trim().toLowerCase());

export type UserRole = "admin" | "program_manager" | "mentor" | "learner" | "unknown";

export function isManagerOrAbove(role: UserRole): boolean {
  return role === "admin" || role === "program_manager";
}

export function resolveRole(claimsRole: UserRole, email: string | null | undefined): UserRole {
  if (claimsRole && claimsRole !== "unknown") return claimsRole;
  if (email && PROGRAM_MANAGER_EMAILS.includes(email.toLowerCase())) return "program_manager";
  return "unknown";
}

/** Get PLG names where user is a mentor, from memberships. "KCNA-005" -> "KCNA 005". */
export function getMentorPLGs(memberships: Membership[]): string[] {
  return memberships
    .filter((m) => m.role === "mentor")
    .map((m) => m.tag.replace("-", " "));
}

/** Get first mentor PLG (for redirect). */
export function getMentorPLG(memberships: Membership[]): string | null {
  const plgs = getMentorPLGs(memberships);
  return plgs.length > 0 ? plgs[0] : null;
}
